import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import { useFetchCampaign } from '../../../../hooks/campaign-hooks';
import { BuilderRoot, GhostButton } from './primitives';
import ReferenceObjectsEditor from './ReferenceObjectsEditor';

/**
 * Reference Objects — a top-level tab of the Campaigns admin (peer to Builder and Analytics), the one
 * place to define a campaign's catalogue of reusable attributes (outcomes, capabilities, customer
 * personas, workshop team roles) that its steps then select from. It rides the same `?c=<id>` query
 * param the Builder uses, so switching between the Builder and Reference Objects tabs keeps the same
 * campaign open: no `c` shows the campaign picker; a `c` loads that campaign and renders the catalogue.
 */
export function CampaignReferences(props: { canWrite: boolean }): JSX.Element {
  const { canWrite } = props;
  const [searchParams, setSearchParams] = useSearchParams();

  const cParam = searchParams.get('c');
  const campaignId = cParam && Number.isFinite(Number(cParam)) ? Number(cParam) : null;

  const back = React.useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('c');
    next.delete('step');
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  // Pass undefined (not 0) when no campaign is selected: 0 passes useFetchItem's
  // numeric-id guard and would fire GET /api/db/campaign/0/ (404); undefined disables it.
  const { data: campaign, isLoading } = useFetchCampaign(campaignId ?? undefined);

  // No campaign selected → the global reference-object library (manage the shared catalogue itself,
  // unscoped to any campaign). Selecting a campaign (?c=) drops into that campaign's linked subset.
  if (campaignId == null) {
    return (
      <BuilderRoot>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
              <strong>Reference Objects</strong>
              <span style={{ fontSize: '0.8rem', opacity: 0.7 }}>Global library</span>
            </div>
          </div>
          <ReferenceObjectsEditor campaign={null} canWrite={canWrite} />
        </div>
      </BuilderRoot>
    );
  }

  return (
    <BuilderRoot>
      {isLoading || !campaign ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <CircularProgress />
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <GhostButton onClick={back}>← All campaigns</GhostButton>
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
              <strong>{campaign.name}</strong>
              <span style={{ fontSize: '0.8rem', opacity: 0.7 }}>Reference Objects</span>
            </div>
          </div>
          <ReferenceObjectsEditor campaign={campaign} canWrite={canWrite} />
        </div>
      )}
    </BuilderRoot>
  );
}

export default CampaignReferences;
